import { isSpecKeyHidden, titleCaseValue } from "@/lib/spec-display-policy"
import { formatSpecValue } from "@/lib/format-utils"

interface TechSpecsCardProps {
  /** Key-value spec pairs */
  specs: Record<string, string>
  /** Card heading (defaults to "Technical Specs") */
  title?: string
  /** Additional classes on the wrapper */
  className?: string
}

export function TechSpecsCard({
  specs,
  title = "Technical Specs",
  className = "",
}: TechSpecsCardProps) {
  // Brand Policy filter: drop Amazon-internal / Keepa-internal / pricing / ASIN
  // / item_type / product_type / category / etc keys before render. Same
  // denylist used by components/blocks/technical-specs/index.tsx — single
  // source of truth in lib/spec-display-policy.ts. Without this, brand-kit
  // PDPs leaked Amazon Buy Box Usd / Amazon Subcategory / Amazon Display
  // Group / Item Type / Product Type rows (May 10, 2026 user-flagged
  // regression — the canonical TechnicalSpecs block had the filter; this
  // brand-kit variant did not).
  const visibleSpecs = Object.entries(specs).filter(
    ([key, value]) => !isSpecKeyHidden(key) && value != null && String(value).trim() !== "",
  )

  if (visibleSpecs.length === 0) {
    // Nothing renders if every key was filtered (Real-or-Hidden Rule 3).
    return null
  }

  return (
    <section className={`bg-[#0469f0]/[0.04] rounded-lg p-5 md:p-6 ${className}`}>
      <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
        <span className="text-[#0469f0]">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </span>
        {title}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
        {visibleSpecs.map(([key, value]) => (
          <div key={key} className="flex justify-between py-3 border-b border-gray-200">
            <span className="text-gray-600 font-medium text-sm capitalize">
              {key.replace(/([A-Z])/g, " $1").trim()}
            </span>
            <span className="text-gray-900 font-bold text-sm">
              {typeof value === "string" ? titleCaseValue(formatSpecValue(value)) : formatSpecValue(value)}
            </span>
          </div>
        ))}
      </div>
    </section>
  )
}
